import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Mail, Phone, Globe as GlobeIcon } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, StageBadge, PriorityBadge } from "@/hq/HQPrimitives";
import ActivityTimeline from "@/hq/ActivityTimeline";
import HQInvoicesPanel from "@/hq/HQInvoicesPanel";

function LabelValue({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className="mt-1 text-[15px] text-[#0A0A0A] break-words">{children ?? "—"}</div>
    </div>
  );
}

export default function HQBusinessDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    hqApi.get(`/businesses/${id}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading this business."));
    return () => { alive = false; };
  }, [id]);

  if (err) return <Card className="p-6" data-testid="hq-business-error">{err}</Card>;
  if (!data) return (
    <div className="space-y-4">
      <Skeleton h={40} w={220} />
      <Card className="p-6 space-y-3"><Skeleton h={20}/><Skeleton h={20}/><Skeleton h={20}/></Card>
    </div>
  );

  const { business: b, contacts, websites, services, leads, enquiries, activities } = data;
  const primary = websites?.[0];

  return (
    <div className="space-y-5" data-testid="hq-business-detail">
      <Link to="/hq/businesses" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A] hover:text-[#0A0A0A]">
        <ArrowLeft size={14} /> All businesses
      </Link>

      <Card className="p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Business</div>
            <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">{b.name}</h1>
            <div className="mt-2 text-[13px] text-[#4A4A4A]">
              {b.industry || "—"} · {b.status || "prospect"} · {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
            </div>
          </div>
          {primary && (
            <a href={primary.url || `https://${primary.normalized_domain}`} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#EAEAEA] bg-white text-[12px] tracking-[0.14em] uppercase hover:border-[#FF6A00]">
              <GlobeIcon size={14} /> {primary.normalized_domain}
            </a>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <LabelValue label="Business name">{b.name}</LabelValue>
          <LabelValue label="Legal name">{b.legal_name}</LabelValue>
          <LabelValue label="Industry">{b.industry}</LabelValue>
          <LabelValue label="Country">{b.country}</LabelValue>
          <LabelValue label="Email">{b.email}</LabelValue>
          <LabelValue label="Phone">{b.phone}</LabelValue>
          <LabelValue label="Source">{b.source}</LabelValue>
          <LabelValue label="Address">{b.address}</LabelValue>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Contacts</div>
          <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">People</h2>
          <div className="mt-4 space-y-3">
            {(!contacts || contacts.length === 0) ? (
              <EmptyState title="No contacts" hint="Contacts linked to this business will appear here." />
            ) : contacts.map((c) => (
              <div key={c.id} className="p-3 rounded-xl border border-[#EAEAEA]" data-testid={`hq-biz-contact-${c.id}`}>
                <div className="font-bold text-[14px]">{c.full_name || c.email || "Unnamed"}</div>
                <div className="mt-1 text-[13px] text-[#4A4A4A] space-y-1">
                  {c.email && <div className="flex items-center gap-2"><Mail size={12} />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-2"><Phone size={12} />{c.phone}</div>}
                  {c.job_title && <div className="text-[#8A8A8A]">{c.job_title}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Websites</div>
          <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Digital properties</h2>
          <div className="mt-4 space-y-3">
            {(!websites || websites.length === 0) ? (
              <EmptyState title="No websites" hint="Websites will appear here." />
            ) : websites.map((w) => (
              <div key={w.id} className="p-3 rounded-xl border border-[#EAEAEA]" data-testid={`hq-biz-website-${w.id}`}>
                <div className="font-bold text-[14px] break-all">{w.normalized_domain}</div>
                <div className="mt-1 text-[12px] text-[#4A4A4A] break-all">{w.url || "—"}</div>
                <div className="mt-1 text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">{w.platform || "unknown"}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Services</div>
          <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Current relationships</h2>
          <div className="mt-4 space-y-2">
            {(!services || services.length === 0) ? (
              <EmptyState title="No services" hint="Business-service links will appear here." />
            ) : services.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-[#EAEAEA]">
                <div className="font-bold text-[14px]">{s.service?.name || "—"}</div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A]">{s.status || "—"}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Leads</div>
          <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">All leads for this business</h2>
          <div className="mt-4 space-y-2">
            {(!leads || leads.length === 0) ? (
              <EmptyState title="No leads" hint="New leads for this business will appear here." />
            ) : leads.map((l) => (
              <Link key={l.id} to={`/hq/leads/${l.id}`}
                    className="flex items-center justify-between p-3 rounded-xl border border-[#EAEAEA] hover:border-[#FF6A00]"
                    data-testid={`hq-biz-lead-${l.id}`}>
                <div>
                  <div className="font-bold text-[14px]">{l.service?.name || "—"}</div>
                  <div className="text-[11px] tracking-[0.16em] uppercase text-[#8A8A8A]">
                    {new Date(l.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StageBadge value={l.stage} />
                  <PriorityBadge value={l.priority} />
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Enquiries</div>
        <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">All enquiries for this business</h2>
        <div className="mt-4">
          {(!enquiries || enquiries.length === 0) ? (
            <EmptyState title="No enquiries yet" hint="Enquiries linked to this business will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Type</th>
                    <th className="text-left px-4 py-2 font-bold">Service</th>
                    <th className="text-left px-4 py-2 font-bold">Status</th>
                    <th className="text-left px-4 py-2 font-bold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-t border-[#EAEAEA]">
                      <td className="px-4 py-2">{e.enquiry_type || "—"}</td>
                      <td className="px-4 py-2">{e.service?.name || "—"}</td>
                      <td className="px-4 py-2">{e.status || "—"}</td>
                      <td className="px-4 py-2 text-[#8A8A8A]">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Timeline</div>
        <h2 className="mt-1 font-black text-lg tracking-[-0.01em]">Full activity</h2>
        <div className="mt-6"><ActivityTimeline items={activities || []} /></div>
      </Card>

      <HQInvoicesPanel businessId={b.id} title="Invoices & Payments" createLabel="Create invoice" />

      {(services || []).map((svc) => (
        svc.service_id ? (
          <HQInvoicesPanel key={svc.service_id} businessId={b.id} projectId={svc.service_id}
            title={`Project · ${svc.service?.name || "Service"}`}
            createLabel="Create payment schedule" />
        ) : null
      ))}
    </div>
  );
}
